import axios from 'axios';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { coerceListingPayload } from './auctionMoney';

const defaultApiUrl = typeof window !== 'undefined' && window.location.hostname !== 'localhost'
  ? '/api'
  : 'http://localhost:4000/api';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || defaultApiUrl,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Auto-refresh on 401 TOKEN_EXPIRED
let isRefreshing = false;
let queue = [];
// Set while logging out so in-flight 401s cannot mint a new session
let refreshDisabled = false;

export function setAuthRefreshDisabled(disabled) {
  refreshDisabled = !!disabled;
}

const processQueue = (error) => {
  queue.forEach(p => error ? p.reject(error) : p.resolve());
  queue = [];
};

const isLogoutOrRefreshRequest = (config) => {
  const url = config?.url || '';
  return url.includes('/auth/logout') || url.includes('/auth/refresh');
};

// The AuthContext session probe (GET /auth/me on load). If its refresh is refused the visitor is
// simply signed out (AuthContext shows the signed-out UI); don't bounce them to /login from
// whatever page they opened.
const isSessionProbe = (config) => /\/auth\/me\/?$/.test((config?.url || '').split('?')[0]);

// ── 429 handling (QA r12 O1) ─────────────────────────────────────
// A rate-limited request shows a friendly "Too many requests, try again in Ns" toast (one at a
// time) and sets a shared "rate limited until" time, so loading screens can show the same hint
// (useRateLimitNotice) instead of an endless "Loading…". The error is still passed to the caller,
// with err.rateLimited = true and err.retryAfterSeconds; a 429 never logs the user out.
let rateLimitedUntil = 0;
const rateLimitListeners = new Set();

export function rateLimitMessage(seconds) {
  return `Too many requests, try again in ${Math.max(1, Math.ceil(seconds))}s`;
}

export function retryAfterSeconds(err) {
  const h = Number.parseFloat(err?.response?.headers?.['retry-after']);
  if (Number.isFinite(h) && h > 0) return Math.ceil(h);
  const b = Number.parseFloat(err?.response?.data?.retryAfterSeconds);
  if (Number.isFinite(b) && b > 0) return Math.ceil(b);
  return 60;
}

function noteRateLimit(err) {
  if (err?.response?.status !== 429) return;
  const seconds = retryAfterSeconds(err);
  err.rateLimited = true;
  err.retryAfterSeconds = seconds;
  const data = err.response.data;
  // Keep a specific server message (e.g. failed-login lockout); replace the generic one.
  if (data && typeof data === 'object' && (!data.error || data.error === 'Too many requests')) {
    data.error = rateLimitMessage(seconds);
  }
  rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + seconds * 1000);
  rateLimitListeners.forEach((f) => { try { f(); } catch { /* ignore */ } });
  const msg = data && typeof data === 'object' && data.code === 'login_rate_limited' && data.error
    ? data.error : rateLimitMessage(seconds);
  try { toast.error(msg, { id: 'rate-limited', duration: Math.min(seconds * 1000, 8000) }); } catch { /* no toaster */ }
}

export function getRateLimitSecondsLeft() {
  return Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000));
}

// "Too many requests, try again in Ns" while rate limited (counts down), else null.
export function useRateLimitNotice() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    rateLimitListeners.add(bump);
    const t = setInterval(() => { if (rateLimitedUntil > Date.now() - 1500) bump(); }, 1000);
    return () => { rateLimitListeners.delete(bump); clearInterval(t); };
  }, []);
  const left = getRateLimitSecondsLeft();
  return left > 0 ? rateLimitMessage(left) : null;
}

API.interceptors.response.use(
  res => {
    if (res?.data) res.data = coerceListingPayload(res.data);
    return res;
  },
  async err => {
    const original = err.config;
    noteRateLimit(err);
    if (
      !refreshDisabled &&
      !isLogoutOrRefreshRequest(original) &&
      err.response?.status === 401 &&
      err.response?.data?.code === 'TOKEN_EXPIRED' &&
      !original._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then(() => API(original)).catch(e => Promise.reject(e));
      }

      original._retry = true;
      isRefreshing = true;

      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL || defaultApiUrl}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        processQueue(null);
        return API(original);
      } catch (refreshErr) {
        noteRateLimit(refreshErr);
        processQueue(refreshErr);
        // Prod smoke #13: only a refused refresh (401 / 403) ends the session. A rate limit (429),
        // server error or network error is passed to the caller without sending the user to /login.
        const st = refreshErr?.response?.status;
        if ((st === 401 || st === 403) && !isSessionProbe(original)) window.location.href = '/login';
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export default API;
